import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, Briefcase, Info, AlertTriangle, CheckCircle, Trash2, Check, RefreshCw } from 'lucide-react'
import { supabaseClient } from '../config/supabase'
import { useAuthStore } from '../store/authStore'
import type { Notification } from '../types'

const typeIcons: Record<string, React.ElementType> = {
  info: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  error: AlertTriangle,
  job_match: Briefcase,
  survey: Info,
  system: Info,
}

const typeColors: Record<string, string> = {
  info: 'text-blue-500 bg-blue-50',
  success: 'text-green-500 bg-green-50',
  warning: 'text-amber-500 bg-amber-50',
  error: 'text-red-500 bg-red-50',
  job_match: 'text-purple-500 bg-purple-50',
  survey: 'text-indigo-500 bg-indigo-50',
  system: 'text-gray-500 bg-gray-50',
}

const typeBadgeColors: Record<string, string> = {
  info: 'text-blue-600 bg-blue-100',
  success: 'text-green-600 bg-green-100',
  warning: 'text-amber-600 bg-amber-100',
  error: 'text-red-600 bg-red-100',
  job_match: 'text-purple-600 bg-purple-100',
  survey: 'text-indigo-600 bg-indigo-100',
  system: 'text-gray-600 bg-gray-100',
}

const typeLabels: Record<string, string> = {
  info: 'Info',
  success: 'Success',
  warning: 'Warning',
  error: 'Error',
  job_match: 'Job Match',
  survey: 'Survey',
  system: 'System',
}

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 7) return `${days}d ago`
  return new Date(dateStr).toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' })
}

const NotificationsPage: React.FC = () => {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'unread'>('all')

  const fetchNotifications = useCallback(async () => {
    if (!user?.id) return
    setLoading(true)
    try {
      const { data, error } = await supabaseClient
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })

      if (!error && data) {
        setNotifications(data as Notification[])
      }
    } catch (err) {
      console.warn('Failed to fetch notifications:', err)
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  useEffect(() => {
    fetchNotifications()
  }, [fetchNotifications])

  const markAsRead = async (id: string) => {
    const { error } = await supabaseClient
      .from('notifications')
      .update({ read: true })
      .eq('id', id)
      .eq('user_id', user!.id)

    if (!error) {
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n))
    }
  }

  const markAllRead = async () => {
    if (!user?.id) return
    const { error } = await supabaseClient
      .from('notifications')
      .update({ read: true })
      .eq('user_id', user.id)
      .eq('read', false)

    if (!error) {
      setNotifications(prev => prev.map(n => ({ ...n, read: true })))
    }
  }

  const deleteAllRead = async () => {
    if (!user?.id) return
    const { error } = await supabaseClient
      .from('notifications')
      .delete()
      .eq('user_id', user.id)
      .eq('read', true)

    if (!error) {
      setNotifications(prev => prev.filter(n => !n.read))
    }
  }

  const handleNotificationClick = (notification: Notification) => {
    markAsRead(notification.id)
    const t = notification.title
    if (t === 'Job Invitation' || t === 'Job Invitation No Longer Available' ||
        t === 'Application Update' || t === 'Application Cancelled — Job Listing Closed') {
      navigate('/my-applications')
    } else if (notification.type === 'survey' || t === 'New Survey Available' || t === 'Survey Updated') {
      navigate('/survey')
    } else if (t === 'Feedback Requested') {
      navigate('/provide-feedback')
    } else if (t === 'Feedback Request Accepted' || t === 'Feedback Request Declined' || t === 'Feedback Received') {
      navigate('/employer/feedback')
    } else if (t === 'Registration Approved' || t === 'Registration Update') {
      navigate('/profile')
    } else if (t === 'New Job Application') {
      navigate('/employer/applications')
    } else if (t === 'Job Listing Closed by UCLM Admin') {
      navigate('/employer/jobs')
    } else if (t === 'Partnership Approved' || t === 'Partnership Update') {
      navigate('/employer/jobs')
    }
  }

  const unreadCount = notifications.filter(n => !n.read).length
  const displayed = filter === 'unread' ? notifications.filter(n => !n.read) : notifications

  return (
    <div className="space-y-6">
      {/* Header banner */}
      <div className="bg-blue-900 rounded-2xl p-6 sm:p-8 text-white relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-52 h-52 bg-white/5 rounded-full pointer-events-none" />
        <div className="absolute -bottom-8 right-16 w-40 h-40 bg-yellow-400/10 rounded-full pointer-events-none" />
        <div className="absolute top-4 right-32 w-16 h-16 bg-white/5 rounded-full pointer-events-none" />
        <div className="relative flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
              <Bell className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Notifications</h1>
              <p className="text-blue-200 text-sm mt-1">
                {unreadCount > 0
                  ? `You have ${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}`
                  : "You're all caught up — no unread notifications"}
              </p>
            </div>
          </div>
          <button
            onClick={fetchNotifications}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        {/* Filter tabs */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
          <button
            onClick={() => setFilter('all')}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'all' ? 'bg-white shadow-sm text-blue-700' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            All ({notifications.length})
          </button>
          <button
            onClick={() => setFilter('unread')}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'unread' ? 'bg-white shadow-sm text-blue-700' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Unread ({unreadCount})
          </button>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors"
            >
              <Check className="w-4 h-4" />
              Mark all read
            </button>
          )}
          {notifications.some(n => n.read) && (
            <button
              onClick={deleteAllRead}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Delete read
            </button>
          )}
        </div>
      </div>

      {/* Notifications list */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-24 flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
            <p className="text-sm text-gray-400">Loading notifications...</p>
          </div>
        ) : displayed.length === 0 ? (
          <div className="py-24 text-center">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Bell className="w-8 h-8 text-gray-300" />
            </div>
            <p className="text-gray-700 font-semibold text-base">
              {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
            </p>
            <p className="text-sm text-gray-400 mt-1.5">
              {filter === 'unread' ? "You're all caught up!" : 'Notifications will appear here when there is activity.'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {displayed.map(n => {
              const Icon = typeIcons[n.type] || Info
              const iconColor = typeColors[n.type] || typeColors.info
              const badgeColor = typeBadgeColors[n.type] || typeBadgeColors.info
              return (
                <button
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  className={`w-full text-left px-6 py-5 hover:bg-gray-50 transition-colors flex gap-4 group ${
                    !n.read ? 'bg-blue-50/40' : ''
                  }`}
                >
                  {/* Icon */}
                  <div className={`p-2.5 rounded-xl flex-shrink-0 self-start ${iconColor}`}>
                    <Icon className="w-5 h-5" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <p className={`text-sm leading-snug ${!n.read ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'} group-hover:text-blue-900 transition-colors`}>
                        {n.title}
                      </p>
                      <div className="flex items-center gap-2.5 flex-shrink-0 mt-0.5">
                        <span className="text-xs text-gray-400 whitespace-nowrap">{timeAgo(n.created_at)}</span>
                        {!n.read && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-gray-500 mt-1.5 leading-relaxed">{n.message}</p>
                    <div className="flex items-center gap-2 mt-2.5">
                      <span className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full ${badgeColor}`}>
                        {typeLabels[n.type] || 'Notification'}
                      </span>
                      {!n.read && (
                        <span className="text-[11px] font-semibold text-blue-600 bg-blue-100 px-2.5 py-0.5 rounded-full">
                          Unread
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default NotificationsPage
